/**
 * Mention Autocomplete Component
 * ===============================
 * 
 * Shows participant suggestions when @ is typed in the chat input.
 */

import React, { useState, useEffect, useRef } from 'react';
import './MentionAutocomplete.scss';

interface Participant {
  user_id: string;
  name: string;
  email: string;
}

interface MentionAutocompleteProps {
  participants: Participant[];
  message: string;
  cursorPosition: number;
  onSelect: (participant: Participant) => void;
  onClose: () => void;
}

const MentionAutocomplete: React.FC<MentionAutocompleteProps> = ({
  participants,
  message,
  cursorPosition,
  onSelect,
  onClose
}) => {
  const [selectedIndex, setSelectedIndex] = useState(0);
  const containerRef = useRef<HTMLDivElement>(null);

  // Extract text after @ symbol
  const getSearchTerm = () => {
    const textBeforeCursor = message.substring(0, cursorPosition);
    const lastAtIndex = textBeforeCursor.lastIndexOf('@');
    if (lastAtIndex === -1) return null;

    // Check if search term is part of a word (if there's a space after @, don't show)
    const afterAt = textBeforeCursor.substring(lastAtIndex + 1);
    if (afterAt.includes(' ')) return null;

    const searchTerm = afterAt.toLowerCase().trim();
    // Return empty string if just @ was typed (no search term yet) - this will show all participants
    return searchTerm;
  };

  const searchTerm = getSearchTerm();

  // Filter participants based on search term - show all if searchTerm is empty string
  const filteredParticipants = React.useMemo(() => {
    if (searchTerm === null) return []; // @ not found or space after @

    if (searchTerm === '') {
      // Just @ was typed, show all participants
      return participants;
    }

    // Filter by search term
    return participants.filter(p =>
      p.name.toLowerCase().includes(searchTerm) ||
      p.email.toLowerCase().includes(searchTerm)
    );
  }, [participants, searchTerm]);

  // Reset selected index when filtered results change
  useEffect(() => {
    setSelectedIndex(0);
  }, [filteredParticipants.length]);

  // Handle keyboard navigation
  useEffect(() => {
    if (!searchTerm || filteredParticipants.length === 0) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setSelectedIndex(prev =>
          prev < filteredParticipants.length - 1 ? prev + 1 : 0
        );
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        setSelectedIndex(prev =>
          prev > 0 ? prev - 1 : filteredParticipants.length - 1
        );
      } else if (e.key === 'Enter') {
        e.preventDefault();
        if (filteredParticipants[selectedIndex]) {
          onSelect(filteredParticipants[selectedIndex]);
        }
      } else if (e.key === 'Escape') {
        e.preventDefault();
        onClose();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [searchTerm, filteredParticipants, selectedIndex, onSelect, onClose]);

  // Scroll selected item into view
  useEffect(() => {
    if (containerRef.current) {
      const selectedItem = containerRef.current.children[selectedIndex] as HTMLElement;
      if (selectedItem) {
        selectedItem.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
      }
    }
  }, [selectedIndex]);

  if (searchTerm === null || filteredParticipants.length === 0) {
    return null;
  }

  return (
    <div className="mention-autocomplete" ref={containerRef}>
      {filteredParticipants.map((participant, index) => (
        <div
          key={participant.user_id}
          className={`mention-autocomplete__item ${index === selectedIndex ? 'selected' : ''}`}
          onClick={() => onSelect(participant)}
          onMouseEnter={() => setSelectedIndex(index)}
        >
          <div className="mention-autocomplete__avatar">
            {participant.name.charAt(0).toUpperCase()}
          </div>
          <div className="mention-autocomplete__info">
            <div className="mention-autocomplete__name">{participant.name}</div>
            <div className="mention-autocomplete__email">{participant.email}</div>
          </div>
        </div>
      ))}
    </div>
  );
};

export default MentionAutocomplete;

